import { useState, useEffect } from "react";
import { Draggable } from "react-beautiful-dnd";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	ListItem,
	ListItemIcon,
	ListItemText,
	ListItemSecondaryAction,
	makeStyles,
	TextField,
	Switch
} from "@material-ui/core";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

const useStyles = makeStyles((theme) => ({
	childLink: {
		cursor: "default",
		borderRadius: ".5rem",
		background: theme.palette.background.default,
		margin: ".25rem 0",
		border: "1px solid #bbb",
		"&:hover": {
			background: theme.palette.grey["200"],
		},
	},
	subLink: {
		marginLeft: "2rem",
		maxWidth: "calc(100% - 2rem)"
	},
	childLinkAdd: {
		width: "100%",
		display: "flex",
		justifyContent: "center",
	},
	itemText: {
		//Had to set this because it was overflowing into the ListItemSecondaryAction
		maxWidth: "75%",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
	},
	secondaryAction: {
		"& > *": {
			margin: theme.spacing(1),
		},
	},
}));

function Item({ column, item, i, dndData, setDndData }) {
	const classes = useStyles();
	const [openEditChild, setOpenEditChild] = useState(false);
	const [editChild, setEditChild] = useState();
	const [checked, setChecked] = useState(item.isSubLink ?? false);

	const updateChildLinkState = (e) => {
		e.preventDefault();

		//Change old child link title and url to the new ones
		const removedOldLink = dndData.childLinks;
		delete removedOldLink[item.title];
		const withUpdatedChildLink = {
			...removedOldLink,
			[editChild.title]: {
				title: editChild.title,
				url: editChild.url,
				isSubLink: checked ?? false,
			},
		};

		const editedChildLinkTitles = dndData.mainLinks[
			column.title
		].childLinkTitles.map((value) =>
			value === item.title ? editChild.title : value
		);

		setDndData({
			...dndData,
			childLinks: withUpdatedChildLink,
			mainLinks: {
				...dndData.mainLinks,
				[column.title]: {
					...dndData.mainLinks[column.title],
					childLinkTitles: editedChildLinkTitles,
				},
			},
		});

		setOpenEditChild(false);
		setEditChild();
	};

	const removeChildLink = (mainLinkTitle, childLinkTitle) => {
		const newChildLinks = dndData.childLinks;
		delete newChildLinks[childLinkTitle];
		const newChildLinkTitles = dndData.mainLinks[
			mainLinkTitle
		].childLinkTitles.filter((value) => {
			return value !== childLinkTitle;
		});

		setDndData({
			...dndData,
			childLinks: newChildLinks,
			mainLinks: {
				...dndData.mainLinks,
				[mainLinkTitle]: {
					...dndData.mainLinks[mainLinkTitle],
					childLinkTitles: newChildLinkTitles,
				},
			},
		});
	};

	const handleCloseEditChild = () => {
		setOpenEditChild(false);
	};

	const onChangeEditChild = (e) => {
		setEditChild({ ...editChild, [e.target.name]: e.target.value });
	};


	const onChangeSwitch = (e) => {
		setChecked(!checked);
	};

	useEffect(() => {
		//Change old child link title and url to the new ones
		const withUpdatedSubLinkBool = {
			...dndData.childLinks,
			[item.title]: {
				title: item.title,
				url: item.url,
				isSubLink: checked
			},
		};

		setDndData(
			{
				...dndData,
				childLinks: withUpdatedSubLinkBool
			}
		);


	}, [checked])

	return (
		<Draggable key={item.title} draggableId={item.title} index={i}>
			{(provided) => (
				<ListItem
					ContainerComponent="li"
					ContainerProps={{ ref: provided.innerRef }}
					{...provided.draggableProps}
					dense
					disableRipple
					key={item.title}
					className={`${classes.childLink} ${checked && classes.subLink}`}
					button
					{...provided.dragHandleProps}
				>
					<ListItemIcon>
						<IconButton style={{ cursor: "grab" }} aria-label="move list item">
							<DragIndicatorIcon />
						</IconButton>
					</ListItemIcon>
					<ListItemText>
						<div className={classes.itemText}>
							<span style={{ fontWeight: "bold" }}>{item.title}</span>
							{item.url}
							<Switch checked={checked ?? false} name="isSubLink" onChange={onChangeSwitch} inputProps={{ 'aria-label': 'Set this link as a sub link of the child link' }}>
							</Switch>
						</div>
					</ListItemText>
					<ListItemSecondaryAction className={classes.secondaryAction}>
						<IconButton
							onClick={() => {
								setEditChild({
									title: item.title,
									url: item.url,
								});
								setOpenEditChild(true);
							}}
							edge="end"
							aria-label="edit child link"
						>
							<EditIcon />
						</IconButton>
						<Dialog
							open={openEditChild}
							onClose={() => {
								handleCloseEditChild();
								setEditChild();
							}}
						>
							<form onSubmit={updateChildLinkState}>
								<DialogTitle id="editChildLink">Edit Child Link</DialogTitle>
								<DialogContent>
									<DialogContentText>
										Please add a title and a URL for your child link.
									</DialogContentText>
									<TextField
										value={editChild && editChild.title}
										onChange={onChangeEditChild}
										margin="dense"
										id="editedChildTitle"
										label="Title"
										type="text"
										name="title"
										fullWidth
										required
									/>
									<TextField
										value={editChild && editChild.url}
										margin="dense"
										onChange={onChangeEditChild}
										id="editedChildURL"
										label="URL"
										type="text"
										name="url"
										fullWidth
										required
									/>
								</DialogContent>
								<DialogActions>
									<Button
										onClick={() => {
											handleCloseEditChild();
										}}
										color="primary"
									>
										Cancel
									</Button>
									<Button color="primary" type="submit">
										Update
									</Button>
								</DialogActions>
							</form>
						</Dialog>
						<IconButton
							onClick={() => {
								removeChildLink(column.title, item.title);
							}}
							edge="end"
							aria-label="delete"
						>
							<DeleteIcon />
						</IconButton>
					</ListItemSecondaryAction>
				</ListItem>
			)}
		</Draggable>
	);
}

export default Item;
